import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, Edit, Plus } from 'lucide-react';

interface Country {
  id: number;
  code: string;
  translations: Record<string, string[]>;
}

interface Shop {
  id: number;
  name: string;
  country: {
    id: number;
    code: string;
  };
  image_url?: string;
  created_at: string;
}

const ShopsPage = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    country_id: '',
    image: null as File | null,
  });
  const [imagePreview, setImagePreview] = useState<string>('');

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries();
    fetchShops();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch('/api/app/countries');
      const data = await response.json();
      setCountries(data.countries || []);
    } catch (error) {
      console.error('Hiba az országok lekérésénél:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await fetch('/api/admin/shops');
      const data = await response.json();
      setShops(data.shops || []);
    } catch (error) {
      console.error('Hiba a boltok lekérésénél:', error);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.country_id) {
      alert('Kérlek töltsd ki az összes mezőt!');
      return;
    }

    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append('shop[name]', formData.name);
    formDataToSend.append('shop[country_id]', formData.country_id);
    if (formData.image) {
      formDataToSend.append('shop[image]', formData.image);
    }

    try {
      const url = editingId
        ? `/api/admin/shops/${editingId}`
        : '/api/admin/shops';
      const method = editingId ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Hiba történt');
        return;
      }

      alert(data.message || 'Sikeres mentés!');
      setOpenDialog(false);
      setFormData({ name: '', country_id: '', image: null });
      setImagePreview('');
      setEditingId(null);
      fetchShops();
    } catch (error) {
      console.error('Hiba a mentés során:', error);
      alert('Hiba a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shop: Shop) => {
    setEditingId(shop.id);
    setFormData({
      name: shop.name,
      country_id: shop.country.id.toString(),
      image: null,
    });
    if (shop.image_url) {
      setImagePreview(shop.image_url);
    }
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/admin/shops/${deleteId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Hiba történt');
        return;
      }

      alert(data.message || 'Sikeresen törölve!');
      setDeleteId(null);
      fetchShops();
    } catch (error) {
      console.error('Hiba a törlés során:', error);
      alert('Hiba a törlés során');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', country_id: '', image: null });
    setImagePreview('');
    setEditingId(null);
  };

  const getCountryName = (countryId: number) => {
    const country = countries.find(c => c.id === countryId);
    return country?.code || 'Ismeretlen';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary">Boltok</h1>
          <p className="mt-2 text-foreground/80">Kezeld a bolthálózatokat országok szerint.</p>
        </div>
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingId(null);
                setFormData({ name: '', country_id: '', image: null });
                setImagePreview('');
              }}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Új bolt
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Bolt szerkesztése' : 'Új bolt hozzáadása'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Bolt neve</label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="pl. Lidl"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Ország</label>
                <Select
                  value={formData.country_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, country_id: value })
                  }
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz országot" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id.toString()}>
                        {country.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Kép feltöltése</label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  {imagePreview && (
                    <div className="mb-4">
                      <img
                        src={imagePreview}
                        alt="Előnézet"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={loading}
                  />
                  {editingId && imagePreview && (
                    <p className="text-xs text-foreground/60 mt-2">
                      Új kép feltöltésével felülírhatod a meglévőt
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Mégse
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Mentés...' : 'Mentés'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shops List */}
      <div className="bg-card rounded-lg border">
        {shops.length === 0 ? (
          <div className="p-8 text-center text-foreground/60">
            <p>Még nincsenek felvett boltok.</p>
          </div>
        ) : (
          <div className="divide-y">
            {shops.map((shop) => (
              <div
                key={shop.id}
                className="p-4 flex items-center justify-between hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  {shop.image_url && (
                    <img
                      src={shop.image_url}
                      alt={shop.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium">{shop.name}</p>
                    <p className="text-sm text-foreground/60">
                      {getCountryName(shop.country.id)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(shop)}
                    disabled={loading}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteId(shop.id)}
                    disabled={loading}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>Bolt törlése</AlertDialogTitle>
          <AlertDialogDescription>
            Biztosan törölni szeretnéd ezt a boltot? Ez a művelet nem vonható vissza.
          </AlertDialogDescription>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel disabled={loading}>Mégse</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading ? 'Törlés...' : 'Törlés'}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShopsPage;